'use client';

import React from 'react';
import { IconShare } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/ui';
import { ShareButton } from '@/components/share/ShareButton';

interface TitleContainerProps {
  name: string;
  roles: string[];
  numBuildings: number;
  numUnits: number;
}

/**
 * Title Container - matches Figma design 5979-185923
 * Displays avatar, owner name, metadata (role, buildings, units), and share button
 */
export function TitleContainer({ name, roles, numBuildings, numUnits }: TitleContainerProps) {
  const avatarLabel = name.trim().charAt(0).toUpperCase();
  const primaryRole = roles && roles.length > 0 ? roles[0] : 'Property manager';
  const buildingCount = numBuildings || 0;
  const unitCount = numUnits || 0;

  const ownerDetails = [
    primaryRole,
    buildingCount > 0 && `${buildingCount} building${buildingCount === 1 ? '' : 's'}`,
    unitCount > 0 && `${unitCount} unit${unitCount === 1 ? '' : 's'}`,
  ].filter(Boolean);

  return (
    <div
      className={cn(
        // Mobile: column layout, centered items
        'relative flex flex-col items-center gap-2 pb-3 pt-0 px-2 w-full',
        // Desktop: row layout, left-aligned
        'md:flex-row md:items-center md:gap-4 md:px-0',
      )}
    >
      {/* Avatar - centered on mobile, left-aligned on desktop */}
      <Avatar className="text-heading-5 shrink-0 !size-16" type="Owner" label={avatarLabel} />

      {/* Content - centered on mobile, left-aligned on desktop */}
      <div
        className={cn(
          'flex flex-col gap-1',
          // Mobile: centered, full width
          'items-center w-full',
          // Desktop: left-aligned, flexible width
          'md:items-start md:flex-1 md:min-w-0',
        )}
      >
        <h1
          className={cn(
            'text-heading-5 text-primary',
            // Mobile: centered, can wrap
            'text-center',
            // Desktop: left-aligned, no wrap
            'md:text-left md:whitespace-nowrap',
          )}
        >
          {name}
        </h1>
        <div
          className={cn(
            'flex items-center gap-1 text-body-2 text-secondary',
            // Mobile: centered, can wrap
            'justify-center flex-wrap',
            // Desktop: left-aligned, no wrap
            'md:justify-start',
          )}
        >
          {ownerDetails.map((detail, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span>·</span>}
              <span
                className={cn(
                  'overflow-hidden text-ellipsis',
                  // Mobile: can wrap
                  '',
                  // Desktop: no wrap
                  'md:whitespace-nowrap',
                )}
              >
                {detail}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Share button - absolutely positioned on mobile, normal flow on desktop */}
      <ShareButton
        className={cn(
          'p-3 rounded-full hover:bg-secondary transition-colors shrink-0',
          'flex items-center justify-center',
          // Mobile: absolutely positioned at top right
          'absolute right-4 top-0',
          // Desktop: normal flow
          'md:relative md:right-auto md:top-auto',
        )}
        aria-label="Share owner profile"
        title={name}
        icon={<IconShare className="w-4 h-4 icon-primary" />}
      />
    </div>
  );
}
